import React, {useCallback, useEffect, useRef} from "react";
import {Link, useNavigate} from "react-router-dom";
import moment from "moment";
import Box from "@mui/material/Box";
import CustomDataTable from "../../../../shared/tables/CustomDataTable";
import GenericButton from "../../../../shared/ui/GenericButton";
import {useAuth} from "../../../../hooks/useAuth";
import useDataTable from "../../../../hooks/useDataTable";
import {APIGetCustomSubcategories} from "../api/customSubcategories";
import {APIArchiveCustomSubcategory} from "../api/customSubcategory";

export interface ISubcategory {
    id: number;
    name: string;
    subject: string;
    grade: string;
    created_at: string;
    user_id: string;
}

export const SUBCATEGORIES_COLUMNS = [
    {id: "name", label: "Nume categorie", noWrap: true, tooltip: true, sortable: true, width: "200px", tooltipTextLength: 21,},
    {id: "subject", label: "Disciplină", noWrap: true, sortable: true, width: "130px"},
    {id: "grade", label: "An", noWrap: true, sortable: true, width: "40px"},
    {id: "created_at", label: "Creat la", sortable: true, width: "80px"},
    {id: "id", label: "ID", numeric: true, noWrap: true, sortable: true, width: "70px"},
    {id: "actions", label: "Acțiuni", numeric: true, sticky: true, width: "80px"},
];

export const SUBCATEGORIES_COLUMNS_PRINCIPAL = [
    {id: "name", label: "Nume categorie", noWrap: true, tooltip: true, sortable: true, width: "200px", tooltipTextLength: 21,},
    {id: "subject", label: "Disciplină", noWrap: true, sortable: true, width: "130px"},
    {id: "grade", label: "An", noWrap: true, sortable: true, width: "40px"},
    {id: "created_at", label: "Creat la", sortable: true, width: "80px"},
    {id: "id", label: "ID", numeric: true, noWrap: true, sortable: true, width: "70px"},
];

const SubcategoriesActive = () => {
    const {update, user} = useAuth();
    const navigate = useNavigate();
    const isTeacher = user.position === "teacher";

    const {
        loading,
        setLoading,
        tableData,
        setTableData,
        tableColumns,
        setTableColumns,
        totalRows,
        setTotalRows,
        perPage,
        setPerPage,
        page,
        setPage,
        query,
        submittedQuery,
        orderBy,
        order,
        handleChangePage,
        handleRequestSort,
        handleSearch,
        handleSearchChange,
        handleClearSearch,
    } = useDataTable("", "created_at", undefined, "");

    const pageRef = useRef(page);

    useEffect(() => {
        pageRef.current = page;
    }, [page]);

    const getSubcategories = (currentPage = pageRef.current) => {
        APIGetCustomSubcategories({
            page: `?page=${currentPage + 1}`,
            sort: orderBy,
            order: order,
            search: submittedQuery
        })
            .then((response) => {
                if (response.data.success) {
                    const pageData = response.data.data;

                    if(pageData.data.length === 0 && currentPage > 0) {
                        const PreviousPage = currentPage - 1;

                        setPage(PreviousPage);
                        getSubcategories();
                        return;
                    }

                    setTableData(response.data.data.data.data.map((subcategory: any) => mapSubcategories(subcategory)));
                    setTotalRows(response.data.data.data.total);
                    setPerPage(response.data.data.data.per_page);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch(() => {
                // console.log("getTeachers ERR", err);
                update("error", {show: true, code: "A001"});
            })
            .finally(() => setLoading(false));
    }

    const mapSubcategories = useCallback((subcategory: ISubcategory) => {

        const actions = (
            <Box display="flex" justifyContent="end">
                <Box mr={1}>
                    <GenericButton
                        disabled={user.id !== subcategory.user_id}
                        type="edit-small"
                        onClick={() => editItemHandler(subcategory.id)}
                    />
                </Box>
                <Box>
                    <GenericButton
                        disabled={user.id !== subcategory.user_id}
                        type="archive"
                        onClick={() => confirmArchive(subcategory)}/>
                </Box>
            </Box>
        );

        return {
            id: subcategory.id,
            name: <Link to={`/subcategorii-proprii/${subcategory.id}`}>{subcategory.name}</Link>,
            subject: subcategory.subject,
            grade: subcategory.grade,
            created_at: moment(subcategory.created_at).locale("ro").format("DD.MM.YYYY"),
            ...(isTeacher && {actions})
        }
    }, [user.id]);


    useEffect(() => {
        setLoading(true);
        getSubcategories();
    }, [page, order, orderBy, submittedQuery]);

    useEffect(() => {
        const columns = isTeacher ? SUBCATEGORIES_COLUMNS : SUBCATEGORIES_COLUMNS_PRINCIPAL;
        setTableColumns(columns);
    }, [user.position]);

    const editItemHandler = (id: number) => {
        navigate(`/subcategorii-proprii/${id}/editare`);
    };

    const archiveCategory = (id: string, callback: Function) => {
        update("loading", {show: true, modal: true});

        APIArchiveCustomSubcategory({id})
            .then((response) => {
                if (response.data.success) getSubcategories(pageRef.current);
                else update("error", {show: true, code: "A002"});

                callback();
            })
            .catch(() => update("error", {show: true, code: "A001"}))
            .finally(() =>  update("loading", {show: false}))
    };

    const confirmArchive = (item: {
        id: number;
        name: string;
        [key: string]: any;
    }) => {
        update("confirm", {
            show: true,
            closable: true,
            icon: "fat box-archive",
            title: "Ești sigur că vrei să arhivezi această subcategorie?",
            message: (
                <>
                    Subcategoria <b>{item.name}</b> va fi arhivată. Veți regăsi această subcategorie în
                    secțiunea <b>Subcategorii arhivate.</b>
                </>
            ),
            returnFunction: (callback: Function) => {
                archiveCategory(item.id.toString(), callback);
            },
            buttonYes: "Arhivează",
            buttonNo: "Închide",
        });
    };

    return (
        <CustomDataTable
            loading={loading}
            columns={tableColumns}
            rows={tableData}
            rowsPerPage={perPage}
            page={page}
            totalRows={totalRows}
            onPageChange={handleChangePage}
            handleRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
            hasSearch={true}
            onSearch={handleSearch}
            onChange={handleSearchChange}
            searchValue={query}
            onClearSearch={handleClearSearch}
        />
    );
};

export default SubcategoriesActive;